import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { ProductCard } from '@/components/products/ProductCard';
import { ProductFilters, FilterState } from '@/components/products/ProductFilters';
import { useProducts } from '@/hooks/useApi.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

// Using the same category data as CategoryShowcase  
const categories = [
  { name: 'Boys', link: '/category/boys' },
  { name: 'Girls', link: '/category/girls' },
  { name: 'Ladies', link: '/category/ladies' },
  { name: 'Men', link: '/category/men' },
];

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const filterParam = searchParams.get('filter') || '';
  const [searchQuery, setSearchQuery] = useState(query);
  const [page, setPage] = useState(0);

  // Fetch all products for search and filtering
  const { data: productsData, isLoading } = useProducts({
    search: query || undefined,
    page,
    size: 50, // Get more products for better search results
  });

  const products = productsData?.content || [];

  // Extract available filter options from fetched products
  const availableBrands = useMemo(() => 
    [...new Set(products.map(p => p.brand).filter(Boolean))].sort(), 
    [products]
  );
  
  const availableSizes = useMemo(() => {
    const sizes = new Set<string>();
    products.forEach(p => {
      if (p.variants) {
        p.variants.forEach(variant => {
          if (variant.size) sizes.add(variant.size);
        });
      }
    });
    return [...sizes].sort();
  }, [products]);
  
  const availableColors = useMemo(() => {
    const colors = new Set<string>();
    products.forEach(p => {
      if (p.variants) {
        p.variants.forEach(variant => {
          if (variant.color) colors.add(variant.color);
        });
      }
    });
    return [...colors].sort();
  }, [products]);
  
  const maxPrice = useMemo(() => 
    products.length > 0 ? Math.max(...products.map(p => p.currentPrice || p.salePrice || 0)) : 5000, 
    [products]
  );

  const [filters, setFilters] = useState<FilterState>({
    priceRange: [0, maxPrice],
    brands: [],
    sizes: [],
    colors: [],
    ratings: [],
    discount: 0,
    sortBy: 'featured'
  });

  // Update max price when products load
  useEffect(() => {
    if (maxPrice > 0 && filters.priceRange[1] === 0) {
      setFilters(prev => ({ ...prev, priceRange: [0, maxPrice] }));
    }
  }, [maxPrice]);

  // Filter and sort products client-side for better UX
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Apply trending filter if set
    if (filterParam === 'trending') {
      filtered = filtered.filter(p => (p.rating || 0) >= 4.0);
    }

    // Price filter
    filtered = filtered.filter(p => {
      const price = p.currentPrice || p.salePrice || 0;
      return price >= filters.priceRange[0] && price <= filters.priceRange[1];
    });

    // Brand filter
    if (filters.brands.length > 0) {
      filtered = filtered.filter(p => p.brand && filters.brands.includes(p.brand));
    }

    // Size filter
    if (filters.sizes.length > 0) {
      filtered = filtered.filter(p => 
        p.variants && p.variants.some(v => v.size && filters.sizes.includes(v.size))
      );
    }

    // Color filter
    if (filters.colors.length > 0) {
      filtered = filtered.filter(p => 
        p.variants && p.variants.some(v => v.color && filters.colors.includes(v.color))
      );
    }

    // Rating filter
    if (filters.ratings.length > 0) {
      const minRating = Math.min(...filters.ratings);
      filtered = filtered.filter(p => (p.rating || 0) >= minRating);
    }

    // Discount filter
    if (filters.discount > 0) {
      filtered = filtered.filter(p => {
        const current = p.currentPrice || p.salePrice || 0;
        const original = p.originalPrice || p.mrp || current;
        const discount = original > current ? ((original - current) / original) * 100 : 0;
        return discount >= filters.discount;
      });
    }

    // Sort
    switch (filters.sortBy) {
      case 'price-low':
        filtered.sort((a, b) => (a.currentPrice || a.salePrice || 0) - (b.currentPrice || b.salePrice || 0));
        break;
      case 'price-high':
        filtered.sort((a, b) => (b.currentPrice || b.salePrice || 0) - (a.currentPrice || a.salePrice || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'newest':
        // Assume newer products have higher IDs
        filtered.sort((a, b) => (b.productId || 0) - (a.productId || 0));
        break;
      case 'discount':
        filtered.sort((a, b) => {
          const discountA = a.originalPrice && a.currentPrice ? ((a.originalPrice - a.currentPrice) / a.originalPrice) * 100 : 0;
          const discountB = b.originalPrice && b.currentPrice ? ((b.originalPrice - b.currentPrice) / b.originalPrice) * 100 : 0;
          return discountB - discountA;
        });
        break;
    }

    return filtered;
  }, [products, filters, filterParam]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery });
    } else {
      setSearchParams({});
    }
  };

  const clearFilters = () => {
    setFilters({
      priceRange: [0, maxPrice],
      brands: [],
      sizes: [],
      colors: [],
      ratings: [],
      discount: 0,
      sortBy: 'featured'
    });
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Input
              type="search"
              placeholder='Search "Sarees", "T Shirts", "Kurtas"...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 h-14 rounded-full border-2 border-secondary focus:border-primary text-lg"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground" />
          </div>
        </form>

        {/* Category Quick Navigation */}
        {!query && !filterParam && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Shop by Category</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories.map((category, index) => (
                <motion.div
                  key={category.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={category.link}
                    className="group relative block aspect-[4/3] rounded-xl overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 border hover:border-primary/30 transition-all"
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <span className="text-primary font-semibold text-lg">{category.name}</span>
                        <ChevronRight className="h-5 w-5 text-primary mx-auto mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold">
            {filterParam === 'trending' 
              ? 'Trending Now'
              : query 
                ? filteredProducts.length > 0 
                  ? `Results for "${query}"`
                  : `No results for "${query}"`
                : 'All Products'
            }
          </h1>
          <p className="text-muted-foreground mt-1">
            {isLoading ? 'Loading...' : `${filteredProducts.length} products found`}
          </p>
        </div>

        {/* Mobile controls */}
        <div className="flex lg:hidden items-center gap-4 mb-6">
          <ProductFilters
            filters={filters}
            onFiltersChange={setFilters}
            availableBrands={availableBrands}
            availableSizes={availableSizes}
            availableColors={availableColors}
            maxPrice={maxPrice}
            productCount={filteredProducts.length}
          />
          <Select 
            value={filters.sortBy} 
            onValueChange={(value) => setFilters({ ...filters, sortBy: value })}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price-low">Price: Low-High</SelectItem>
              <SelectItem value="price-high">Price: High-Low</SelectItem>
              <SelectItem value="rating">Top Rated</SelectItem>
              <SelectItem value="discount">Best Discount</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Main content */}
        <div className="flex gap-8">
          {/* Desktop Filters */}
          <ProductFilters
            filters={filters}
            onFiltersChange={setFilters}
            availableBrands={availableBrands}
            availableSizes={availableSizes}
            availableColors={availableColors}
            maxPrice={maxPrice}
            productCount={filteredProducts.length}
          />

          {/* Products Grid */}
          <div className="flex-1">
            {/* Desktop sort */}
            <div className="hidden lg:flex justify-end mb-6">
              <Select 
                value={filters.sortBy} 
                onValueChange={(value) => setFilters({ ...filters, sortBy: value })}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Top Rated</SelectItem>
                  <SelectItem value="discount">Best Discount</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="aspect-[3/4] rounded-lg" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-6 w-1/2" />
                  </div>
                ))}
              </div>
            )}

            {/* Products Grid */}
            {!isLoading && filteredProducts.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.productId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && filteredProducts.length === 0 && products.length > 0 && (
              <div className="text-center py-20">
                <p className="text-muted-foreground mb-4">No products match your filters</p>
                <Button variant="outline" onClick={clearFilters}>
                  Clear All Filters
                </Button>
              </div>
            )}

            {/* No search results */}
            {!isLoading && products.length === 0 && query && (
              <div className="text-center py-20">
                <p className="text-muted-foreground mb-4">No products found for "{query}"</p>
                <Button variant="outline" onClick={() => {
                  setSearchQuery('');
                  setSearchParams({});
                }}>
                  Clear Search
                </Button>
              </div>
            )}

            {/* Initial state - no search query */}
            {!isLoading && !query && !filterParam && products.length === 0 && (
              <div className="text-center py-20">
                <Search className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h2 className="text-xl font-semibold mb-2">Search for products</h2>
                <p className="text-muted-foreground">
                  Find sarees, t-shirts, kurtas, suits, and more
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SearchPage;
